import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface CaseStudy {
  Id: number;
  Title: string;
  CaseText: string;
  LearningObjectives: string | null;
  KeyConcepts: string[] | null;
  ExpectedMisconceptions: string[] | null;
  DifficultyLevelTagId: number | null;
  SubjectTagId: number | null;
  CourseId: number | null;
  CreatedByUserId: number | null;
  CreatedAt: string;
  UpdatedAt: string;
  DeleteStatus: number;
  // Present only on cases returned by listForStep (from the step attachment):
  RunMode?: "discussion" | "response";
  Participation?: "individual" | "group";
}

export interface CaseStudyInput {
  title: string;
  caseText: string;
  learningObjectives?: string;
  keyConcepts?: string[];
  expectedMisconceptions?: string[];
  difficultyLevelTagId?: number | null;
  subjectTagId?: number | null;
  courseId?: number | null;
}

export interface InstructorCourse {
  id: number;
  name: string;
  shortCode: string | null;
}

export interface CaseResponse {
  responseId: number;
  caseId: number;
  stepId: number;
  batchId: number | null;
  userId: number;
  enquiryId: number | null;
  groupId: number | null;
  studentName: string | null;
  groupName: string | null;
  responseText: string;
  status: "submitted" | "evaluated";
  score: number | null;
  teacherFeedback: string | null;
  submittedAt: string | null;
  evaluatedAt: string | null;
}

class CaseStudyService {
  private baseUrl = `${Config.nodeSiteUrl}v1/case-studies`;
  private flipClassBase = `${Config.nodeSiteUrl}v1/flip-class`;

  async list(opts?: {
    subjectTagId?: number;
    difficultyTagId?: number;
    courseId?: number;
    q?: string;
  }): Promise<{ success: boolean; data: { cases: CaseStudy[] } }> {
    const response = await myAxios.get(this.baseUrl, { params: opts || {} });
    return response.data as any;
  }

  async listInstructorCourses(): Promise<{
    success: boolean;
    data: { mode: "instructor" | "admin"; courses: InstructorCourse[] };
  }> {
    const response = await myAxios.get(`${this.baseUrl}/instructor-courses`);
    return response.data as any;
  }

  async resolveCourseFromContext(opts: {
    stepId?: number;
    batchId?: number;
  }): Promise<{ success: boolean; data: { courseId: number | null } }> {
    const response = await myAxios.get(`${this.baseUrl}/resolve-course`, {
      params: opts,
    });
    return response.data as any;
  }

  async get(id: number): Promise<{ success: boolean; data: CaseStudy }> {
    const response = await myAxios.get(`${this.baseUrl}/${id}`);
    return response.data as any;
  }

  async create(input: CaseStudyInput): Promise<{ success: boolean; data: CaseStudy }> {
    const response = await myAxios.post(this.baseUrl, input);
    return response.data as any;
  }

  async generate(input: {
    topic: string;
    subject?: string;
    ageBand?: string;
    difficulty?: string;
    focus?: string;
  }): Promise<{
    success: boolean;
    data: {
      title: string;
      caseText: string;
      learningObjectives: string;
      keyConcepts: string[];
      expectedMisconceptions: string[];
      model: string;
      processingMs: number;
    };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/generate`, input, {
      timeout: 60000,
    });
    return response.data as any;
  }

  async update(id: number, input: Partial<CaseStudyInput>): Promise<{ success: boolean; data: CaseStudy }> {
    const response = await myAxios.put(`${this.baseUrl}/${id}`, input);
    return response.data as any;
  }

  async delete(id: number): Promise<{ success: boolean }> {
    const response = await myAxios.delete(`${this.baseUrl}/${id}`);
    return response.data as any;
  }

  async listForStep(stepId: number): Promise<{ success: boolean; data: { cases: CaseStudy[] } }> {
    const response = await myAxios.get(`${this.flipClassBase}/step/${stepId}/cases`);
    return response.data as any;
  }

  async attachToStep(
    stepId: number,
    caseId: number,
    opts?: {
      sortOrder?: number;
      runMode?: "discussion" | "response";
      participation?: "individual" | "group";
    },
  ): Promise<{ success: boolean }> {
    const response = await myAxios.post(`${this.flipClassBase}/step/${stepId}/cases`, {
      caseId,
      sortOrder: opts?.sortOrder,
      runMode: opts?.runMode,
      participation: opts?.participation,
    });
    return response.data as any;
  }

  async detachFromStep(stepId: number, caseId: number): Promise<{ success: boolean }> {
    const response = await myAxios.delete(`${this.flipClassBase}/step/${stepId}/cases/${caseId}`);
    return response.data as any;
  }

  /* ---- Self-paced case responses (teacher correction) ---- */

  async listResponses(
    caseId: number,
    opts?: { stepId?: number; batchId?: number },
  ): Promise<{ success: boolean; data: { responses: CaseResponse[] } }> {
    const response = await myAxios.get(`${this.baseUrl}/${caseId}/responses`, {
      params: opts || {},
    });
    return response.data as any;
  }

  async evaluateResponse(
    responseId: number,
    input: { feedback?: string; score?: number | null },
  ): Promise<{ success: boolean }> {
    const response = await myAxios.post(
      `${this.baseUrl}/responses/${responseId}/evaluate`,
      input,
    );
    return response.data as any;
  }
}

export default new CaseStudyService();
